import { type AnimationEvent, useEffect, useRef, useState } from "react";
import { haptic } from "../../telegram";
import {
  type Dir,
  type DuelView,
  HOLD_MS,
  segments,
  SLIDE_MS,
  truthAt,
  VERDICT_MS,
  verdictAt,
} from "./duel";
import { DuelHalf, reduceMotion } from "./DuelHalf";
import { Check, Cross } from "./icons";
import "./duel.css";

type Phase = "ask" | "reveal" | "slide";

interface DuelStageProps {
  view: DuelView;
  busy: boolean;
  onPick: (dir: Dir) => void;
}

/**
 * The duel: known country on top, the one to judge below, VS on the seam. An answer
 * counts the hidden number up, VS turns ✓/✗, then the lower half slides up to become
 * the next known country. Tapping the stage after the verdict skips the wait.
 */
export function DuelStage({ view, busy, onPick }: DuelStageProps) {
  const answered = view.picks.length;
  const done = answered >= view.total;
  const [shown, setShown] = useState(() => Math.min(answered, view.total - 1));
  const [phase, setPhase] = useState<Phase>(done ? "reveal" : "ask");
  const [verdictOn, setVerdictOn] = useState(done);
  const [chosen, setChosen] = useState<Dir | null>(null);
  const step = useRef(shown);
  const timers = useRef<number[]>([]);

  const clear = () => {
    for (const t of timers.current) window.clearTimeout(t);
    timers.current = [];
  };
  useEffect(() => clear, []);

  const advance = (from: number) => {
    if (step.current !== from) return;
    clear();
    step.current = from + 1;
    setShown(from + 1);
    setPhase("ask");
    setVerdictOn(false);
    setChosen(null);
  };

  const slide = (from: number) => {
    clear();
    if (reduceMotion()) return advance(from);
    setPhase("slide");
    // animationend is the cue; this only rescues a missed event.
    timers.current.push(window.setTimeout(() => advance(from), SLIDE_MS + 250));
  };

  // An answer came back: run the reveal for the round on screen.
  useEffect(() => {
    const from = step.current;
    if (answered <= from) return;
    clear();
    if (answered > from + 1 || phase !== "ask") {
      // Rounds answered elsewhere (another phone): jump there, no show.
      const to = Math.min(answered, view.total - 1);
      step.current = to;
      setShown(to);
      setPhase(answered >= view.total ? "reveal" : "ask");
      setVerdictOn(answered >= view.total);
      setChosen(null);
      return;
    }
    const last = answered >= view.total;
    const right = verdictAt(view, from);
    setPhase("reveal");
    timers.current.push(
      window.setTimeout(
        () => {
          setVerdictOn(true);
          // The last round's buzz comes from useGame (won/lost).
          if (!last && right) haptic.success();
          else if (!last) haptic.error();
        },
        reduceMotion() ? 0 : VERDICT_MS,
      ),
    );
    if (!last) timers.current.push(window.setTimeout(() => slide(from), HOLD_MS));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per new answer only
  }, [answered]);

  // A failed request leaves the round unanswered: let both buttons back.
  useEffect(() => {
    if (!busy && answered === step.current && phase === "ask") setChosen(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when a request settles
  }, [busy]);

  const pick = (dir: Dir) => {
    if (busy || chosen || phase !== "ask" || done) return;
    setChosen(dir);
    onPick(dir);
  };

  const skip = () => {
    if (phase === "reveal" && verdictOn && !done && answered === step.current + 1) slide(step.current);
  };

  const onSlideEnd = (e: AnimationEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) advance(shown);
  };

  const right = verdictAt(view, shown);
  const vs = phase === "slide" ? "gone" : phase === "ask" ? (chosen ? "wait" : "vs") : verdictOn ? (right ? "ok" : "bad") : "wait";
  const segs = segments(view, shown, verdictOn);
  const m = view.metric;

  return (
    <div className="du-wrap">
      <div className="du" data-phase={phase} onClick={skip}>
        <div className="du-track" data-sliding={phase === "slide"} onAnimationEnd={onSlideEnd}>
          {[0, 1, 2].map((slot) => {
            const round = view.rounds[shown + slot];
            if (!round) return <div key={`empty-${slot}`} className="du-half" data-slot={slot} />;
            return (
              <DuelHalf
                key={round.code}
                round={round}
                slot={slot}
                metric={m.key}
                phase={phase}
                verdictOn={verdictOn}
                right={right === true}
                truth={truthAt(view, shown)}
                chosen={chosen}
                busy={busy}
                onPick={pick}
              />
            );
          })}
        </div>

        <div className="du-top">
          <span className="du-round">
            {shown + 1}/{view.total}
          </span>
          <span className="du-segs">
            {segs.map((s, i) => (
              <i key={i} className="du-seg" data-s={s} data-fresh={i === shown && verdictOn && phase === "reveal"} />
            ))}
          </span>
          <span className="du-chip">
            {m.icon} {m.label}
          </span>
        </div>

        <div key={`${vs === "wait" ? "vs" : vs}-${shown}`} className={`du-vs du-vs-${vs}`} role="img" aria-label={vs === "ok" ? "вгадано" : vs === "bad" ? "не вгадано" : "проти"}>
          {vs === "ok" ? <Check draw /> : vs === "bad" ? <Cross draw /> : "VS"}
        </div>
      </div>
      <div className="du-rule">
        вгадай щонайменше {view.winAt} з {view.total}
      </div>
    </div>
  );
}
